import React, { useEffect, useState } from 'react';
import StatCard from '../../components/ui/StatCard'; // NOTE: Requires StatCard.jsx to be in ../../components/ui
import { getLeaderModuleResults, getLeaderDeniedRequests } from '../../api/moduleApi';

const LeaderDashboard = ({ user }) => {
  const [stats, setStats] = useState({
    activeSurveys: '0',
    responseRate: 'N/A',
    yoyAvgDelta: 'N/A',
    yoyAvgDeltaClass: 'text-gray-900',
  });
  const [deniedRequests, setDeniedRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const leaderId = (user && (user.account_id || user.id)) || null;
        if (!leaderId) {
          setStats({ activeSurveys: '0', responseRate: 'N/A', yoyAvgDelta: 'N/A', yoyAvgDeltaClass: 'text-gray-900' });
          setDeniedRequests([]);
          return;
        }

        // Fetch survey stats
        const res = await getLeaderModuleResults(leaderId);
        const surveys = [];

        (res || []).forEach(mod => {
          (mod.surveys || []).forEach(s => {
            surveys.push({
              ...s,
              module_id: mod.module_id,
              module_code: mod.module_code,
              module_name: mod.module_name,
            });
          });
        });

        const activeCount = surveys.filter(s => s.status === 'Active').length;

        const latestSurvey = [...surveys].sort((a, b) => {
          const ya = a.survey_year || 0;
          const yb = b.survey_year || 0;
          if (yb !== ya) return yb - ya;
          const da = a.deployment_date ? new Date(a.deployment_date).getTime() : 0;
          const db = b.deployment_date ? new Date(b.deployment_date).getTime() : 0;
          return db - da;
        })[0];

        let responseRate = 'N/A';
        if (latestSurvey && latestSurvey.total_enrolled) {
          const pct = (latestSurvey.responses_completed / latestSurvey.total_enrolled) * 100;
          responseRate = `${Math.round(pct)}%`;
        }

        let yoyAvgDelta = 'N/A';
        let yoyAvgDeltaClass = 'text-gray-900';
        if (latestSurvey && latestSurvey.avg_rating != null) {
          const moduleSurveys = surveys
            .filter(s => s.module_id === latestSurvey.module_id)
            .sort((a, b) => {
              const ya = a.survey_year || 0;
              const yb = b.survey_year || 0;
              if (yb !== ya) return yb - ya;
              const da = a.deployment_date ? new Date(a.deployment_date).getTime() : 0;
              const db = b.deployment_date ? new Date(b.deployment_date).getTime() : 0;
              return db - da;
            });

          if (moduleSurveys.length > 1) {
            const prev = moduleSurveys[1];
            if (prev && prev.avg_rating != null) {
              const delta = latestSurvey.avg_rating - prev.avg_rating;
              yoyAvgDelta = `${delta >= 0 ? '+' : ''}${delta.toFixed(2)}`;
              if (delta > 0) {
                yoyAvgDeltaClass = 'text-green-600';
              } else if (delta < 0) {
                yoyAvgDeltaClass = 'text-red-600';
              } else {
                yoyAvgDeltaClass = 'text-gray-600';
              }
            }
          }
        }

        setStats({
          activeSurveys: String(activeCount),
          responseRate,
          yoyAvgDelta,
          yoyAvgDeltaClass,
        });

        // Fetch denied requests
        try {
          const denied = await getLeaderDeniedRequests(leaderId);
          setDeniedRequests(denied || []);
        } catch (err) {
          console.error('Failed to fetch denied requests:', err);
          setDeniedRequests([]);
        }
      } catch (err) {
        console.error('Failed to load leader stats:', err);
        setStats({ activeSurveys: '0', responseRate: 'N/A', yoyAvgDelta: 'N/A', yoyAvgDeltaClass: 'text-gray-900' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold mb-4 text-indigo-700">Module Leader Dashboard</h2>
      <p className="text-gray-600 mb-6">
        View active surveys, customize module-specific questions, and review your dedicated module data.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title="Active Surveys" value={loading ? 'Loading...' : stats.activeSurveys} iconName="BarChart" />
        <StatCard title="Current Response Rate" value={loading ? 'Loading...' : stats.responseRate} iconName="BarChart" />
        <StatCard
          title="YoY Avg Delta"
          value={loading ? 'Loading...' : <span className={stats.yoyAvgDeltaClass}>{stats.yoyAvgDelta}</span>}
          iconName="BarChart"
        />
      </div>

      {/* Notifications Section */}
      {deniedRequests.length > 0 && (
        <div className="mt-8 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
          <p className="font-semibold text-red-800 mb-3">Denied Survey Requests</p>
          <div className="space-y-3">
            {deniedRequests.map((req) => (
              <div key={req.request_id} className="bg-white p-3 rounded-lg border border-red-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{req.module_name}</p>
                    <p className="text-sm text-gray-600 mt-1">{req.feedback || 'No feedback provided'}</p>
                    {req.actioned_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        Denied on {new Date(req.actioned_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Section */}
      {deniedRequests.length === 0 && (
        <div className="mt-8 p-4 bg-green-50 border-l-4 border-green-500 text-green-800 rounded-lg">
          <p className="font-semibold">No Denied Requests</p>
          <p className="text-sm">All your survey requests have been approved or are pending review.</p>
        </div>
      )}
    </div>
  );
};

export default LeaderDashboard;